import type { Auth } from "@staffsmith/web-auth";

// The operator app's view of the API: /operator/v1/... (services/ats/src/operators.ts;
// QUESTIONS.md Q49), behind the operator pool's sign-in.

export type FieldError = { field: string; message: string };

export type OperatorMe = { id: string; email: string; display_name: string };
export type Plan = "trial" | "standard";
export type Tenant = {
  id: string;
  name: string;
  plan: Plan;
  demo: boolean;
  status: string;
  data_region: string;
  created_at: string;
  admins: { email: string; status: "invited" | "active" | "disabled" }[];
  latest_invoice: { month: string; total_usd: number; status: string } | null;
};
export type TenantInput = { name?: string; plan?: Plan; demo?: boolean };

// An incident and its timeline (services/ats/src/ops.ts).
export type Incident = {
  id: string;
  title: string;
  severity: string;
  status: "investigating" | "mitigated" | "resolved" | "closed";
  drill: boolean;
  public_summary: string | null;
  runbook: string | null;
  declared_at: string;
  resolved_at: string | null;
  closed_at: string | null;
  events: {
    id: string;
    kind: string;
    body: string;
    occurred_at: string;
    operator: string | null;
  }[];
};

// An invoice from StaffSmith to a firm (services/ats/src/platform-console.ts).
export type TenantInvoice = {
  id: string;
  period_start?: string;
  status: "draft" | "issued" | "void";
  lines: { item: string; quantity: number; unit_usd: number; amount_usd: number }[];
  total_usd: number;
  issued_at?: string | null;
};

// Model governance (services/ats/src/governance.ts; roadmap Phase 16). Versions
// are "screening@2": "@" is valid in a path, so it stays as it is.
const seg = (s: string) => encodeURIComponent(s).replaceAll("%40", "@");
export type Gate = { gate: "quality" | "cost" | "bias"; ok: boolean; detail: string };
export type AgentSummary = {
  agent: string;
  definition_version: string;
  live_version: string | null;
  prompt_versions: string[];
};
export type EvalSummary = {
  id: string;
  cases_total: number;
  cases_passed: number;
  cost_per_task_usd: string | number;
  bias_cases_passed: boolean;
  gates: Gate[];
  created_at: string;
};
export type AgentVersion = {
  id: string;
  prompt_version: string;
  status: "candidate" | "live" | "retired";
  promoted_at: string | null;
  accepted_failure_reason: string | null;
  latest_eval: EvalSummary | null;
};
export type EvalRun = EvalSummary & {
  agent: string;
  prompt_version: string;
  model: string;
  results: { id: string; passed: boolean; failures: string[] }[];
};

// An RFC 9457 problem from the API, readable in the UI.
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly title: string,
    readonly detail?: string,
    readonly errors: FieldError[] = [],
    readonly requestId?: string,
  ) {
    super(detail ? `${title}: ${detail}` : title);
  }
}

export function createApi(
  baseUrl: string,
  auth: Auth,
  doFetch: typeof fetch = fetch.bind(globalThis),
) {
  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = await auth.accessToken();
    if (!token) throw new ApiError(401, "Not signed in");
    const res = await doFetch(`${baseUrl}${path}`, {
      method,
      headers: {
        authorization: `Bearer ${token}`,
        ...(body === undefined ? {} : { "content-type": "application/json" }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    const data = (await res.json().catch(() => undefined)) as Record<string, unknown> | undefined;
    if (!res.ok) {
      throw new ApiError(
        res.status,
        typeof data?.title === "string" ? data.title : `Request failed (${res.status})`,
        typeof data?.detail === "string" ? data.detail : undefined,
        Array.isArray(data?.errors) ? (data.errors as FieldError[]) : [],
        res.headers.get("x-request-id") ?? undefined,
      );
    }
    return data as T;
  }

  return {
    me: () => request<OperatorMe>("GET", "/operator/v1/me"),
    tenants: () => request<{ data: Tenant[] }>("GET", "/operator/v1/tenants"),
    tenant: (id: string) => request<Tenant>("GET", `/operator/v1/tenants/${id}`),
    createTenant: (input: TenantInput) => request<Tenant>("POST", "/operator/v1/tenants", input),
    updateTenant: (id: string, input: TenantInput) =>
      request<Tenant>("PATCH", `/operator/v1/tenants/${id}`, input),
    // Creates the admin's sign-in without sending anything: they sign in with an
    // emailed code when they first open the staff app.
    inviteFirstAdmin: (id: string, email: string, displayName: string) =>
      request<Tenant>("POST", `/operator/v1/tenants/${id}/first-admin`, {
        email,
        display_name: displayName,
      }),
    // The console, the rest (Phase 18): domain, flags, usage, invoices, support.
    setDomain: (id: string, domain: string | null, verified = false) =>
      request<{ custom_domain: string | null; domain_status: string }>(
        "PUT",
        `/operator/v1/tenants/${id}/domain`,
        { domain, verified },
      ),
    // A demo tenant's one-press reset (Phase 8), the same as its admins have.
    demoResets: (id: string) =>
      request<{
        resets_enabled: boolean;
        data: {
          id: string;
          status: string;
          step: number;
          steps: string[];
          error: string | null;
          requested_by: string | null;
          created_at: string;
          finished_at: string | null;
        }[];
      }>("GET", `/operator/v1/tenants/${id}/demo-resets`),
    resetDemo: (id: string) =>
      request<{ reset_id: string }>("POST", `/operator/v1/tenants/${id}/demo-resets`, {}),
    flags: (id: string) =>
      request<{
        data: { flag: string; about: string; enabled: boolean; default: boolean; set: boolean }[];
      }>("GET", `/operator/v1/tenants/${id}/flags`),
    setFlag: (id: string, flag: string, enabled: boolean) =>
      request<{ flag: string; enabled: boolean }>(
        "PUT",
        `/operator/v1/tenants/${id}/flags/${flag}`,
        {
          enabled,
        },
      ),
    usage: (id: string, month: string) =>
      request<{ usage: Record<string, number> }>(
        "GET",
        `/operator/v1/tenants/${id}/usage?month=${encodeURIComponent(month)}`,
      ),
    tenantInvoices: (id: string) =>
      request<{ data: TenantInvoice[]; prices_are_placeholders: boolean }>(
        "GET",
        `/operator/v1/tenants/${id}/invoices`,
      ),
    prepareInvoice: (id: string, month: string) =>
      request<TenantInvoice>("POST", `/operator/v1/tenants/${id}/invoices`, { month }),
    issueInvoice: (id: string, invoiceId: string) =>
      request<{ status: string }>("POST", `/operator/v1/tenants/${id}/invoices/${invoiceId}/issue`),
    voidInvoice: (id: string, invoiceId: string) =>
      request<{ status: string }>("POST", `/operator/v1/tenants/${id}/invoices/${invoiceId}/void`),
    support: (id: string) =>
      request<{
        grants: {
          id: string;
          reason: string;
          starts_at: string;
          expires_at: string;
          revoked_at: string | null;
          active: boolean;
        }[];
        uses: { id: string; action: string; occurred_at: string; operator: string }[];
      }>("GET", `/operator/v1/tenants/${id}/support`),
    supportSnapshot: (id: string) =>
      request<{ snapshot: Record<string, unknown> }>(
        "POST",
        `/operator/v1/tenants/${id}/support/snapshot`,
      ),
    // Operations (Phase 19): health, alerts and incidents.
    health: () =>
      request<{
        platform: Record<string, unknown>;
        alarms: {
          alarm_name: string;
          state: string;
          component: string;
          summary: string;
          runbook: string | null;
          occurred_at: string;
        }[];
        recent_alerts: {
          id: string;
          alarm_name: string;
          state: string;
          summary: string;
          runbook: string | null;
          occurred_at: string;
        }[];
      }>("GET", "/operator/v1/health"),
    // Platform starter configurations (design v0.6.3, C11f).
    starterConfigs: () =>
      request<{
        data: {
          key: string;
          label: string;
          config: unknown;
          updated_by: string;
          updated_at: string;
        }[];
      }>("GET", "/operator/v1/starter-configs"),
    saveStarterConfig: (key: string, label: string, config: unknown) =>
      request<{ key: string }>("PUT", `/operator/v1/starter-configs/${key}`, { label, config }),
    incidents: () =>
      request<{
        data: {
          id: string;
          title: string;
          severity: string;
          status: string;
          drill: boolean;
          declared_at: string;
          closed_at: string | null;
        }[];
      }>("GET", "/operator/v1/incidents"),
    incident: (id: string) => request<Incident>("GET", `/operator/v1/incidents/${id}`),
    declareIncident: (input: {
      title: string;
      severity: string;
      drill: boolean;
      alert_id?: string;
    }) => request<{ id: string; runbook: string }>("POST", "/operator/v1/incidents", input),
    incidentEvent: (
      id: string,
      input: { body?: string; status?: string; public_summary?: string },
    ) => request<{ id: string }>("POST", `/operator/v1/incidents/${id}/events`, input),
    closeIncident: (id: string, review: string) =>
      request<{ status: string }>("POST", `/operator/v1/incidents/${id}/close`, { review }),
    agents: () => request<{ data: AgentSummary[] }>("GET", "/operator/v1/agents"),
    agentVersions: (agent: string) =>
      request<{ agent: string; data: AgentVersion[] }>(
        "GET",
        `/operator/v1/agents/${seg(agent)}/versions`,
      ),
    evalRun: (id: string) => request<EvalRun>("GET", `/operator/v1/eval-runs/${id}`),
    // A person's call: a failed gate needs a reason.
    promote: (agent: string, version: string, reason?: string) =>
      request<{ gates_failed: string[] }>(
        "POST",
        `/operator/v1/agents/${seg(agent)}/versions/${seg(version)}/promote`,
        reason ? { reason } : {},
      ),
    rollback: (agent: string) =>
      request<{ rolled_back: string; live: string }>(
        "POST",
        `/operator/v1/agents/${seg(agent)}/rollback`,
      ),
  };
}

export type Api = ReturnType<typeof createApi>;
